import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Hash, Link2, MessagesSquare, Search, UsersRound } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Entrada } from '../../componentes/formulario';
import { TextoCortado } from '../../componentes/texto-cortado';
import { get } from '../../nucleo/api';
import { nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useDebounce, useIntervaloEquipe, useNomesDoChat } from './dados';
import { previaDaUltima, quando } from './textos';
import { iniciais, nomeDoCanalNaTela, type Canal, type Pagina, type ResultadoBusca } from './tipos';
import { carregarMais } from '../../nucleo/foco';

export type EstadoLista = 'carregando' | 'erro' | 'ok';
type FiltroLista = 'todas' | 'nao_lidas' | 'direta' | 'grupo' | 'departamento' | 'arquivadas';
const FILTROS: { chave: FiltroLista; rotulo: string }[] = [
  { chave: 'todas', rotulo: 'lista.todas' },
  { chave: 'nao_lidas', rotulo: 'lista.naoLidas' },
  { chave: 'direta', rotulo: 'lista.diretas' },
  { chave: 'grupo', rotulo: 'lista.grupos' },
  { chave: 'departamento', rotulo: 'lista.departamentos' },
  { chave: 'arquivadas', rotulo: 'lista.arquivadas' },
];

function Filtro({ ativo, aoClicar, children }: { ativo: boolean; aoClicar: () => void; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={aoClicar}
      className={`rounded-full border-2 px-3 py-1 text-xs font-medium ${ativo ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-borda text-tinta-2 hover:text-tinta-1'}`}
    >
      {children}
    </button>
  );
}

/** Avatar do canal: iniciais da outra pessoa (direta), cerquilha (grupo e departamento) ou elo (registro). */
export function AvatarCanal({ canal }: { canal: Pick<Canal, 'tipo' | 'nome' | 'outro'> }) {
  return (
    <span aria-hidden className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-borda bg-sup-3 text-xs font-semibold text-tinta-2">
      {canal.tipo === 'direta' ? iniciais(canal.outro?.nome) : canal.tipo === 'registro' ? <Link2 className="size-4" /> : canal.tipo === 'departamento' ? <UsersRound className="size-4" /> : <Hash className="size-4" />}
    </span>
  );
}

function ItemCanal({ canal, selecionado, aoEscolher, nomes }: { canal: Canal; selecionado: boolean; aoEscolher: () => void; nomes: readonly string[] }) {
  const { t } = useTranslation('equipe');
  const eu = useSessao().usuario.id;
  const nome = nomeDoCanalNaTela(canal, nomes);
  const previa = previaDaUltima(t, canal.ultima, eu, canal.tipo !== 'direta', nomes);
  return (
    <li>
      <button
        type="button"
        data-canal={canal.id}
        aria-current={selecionado}
        onClick={aoEscolher}
        className={`flex w-full min-w-0 items-start gap-3 border-l-2 px-3 py-3 text-left hover:bg-sup-2 ${selecionado ? 'border-acento-forte bg-sup-2' : 'border-transparent'}`}
      >
        <AvatarCanal canal={canal} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex min-w-0 items-baseline justify-between gap-2">
            <span className={`truncate text-sm text-tinta-1 ${canal.nao_lidas ? 'font-semibold' : 'font-medium'}`} title={nome}>
              {nome}
            </span>
            <span className="numero shrink-0 text-xs text-tinta-3">{quando(canal.ultima?.created_at ?? canal.created_at)}</span>
          </span>
          <span className="flex min-w-0 items-center justify-between gap-2">
            <span className={`truncate text-sm ${canal.nao_lidas ? 'text-tinta-1' : 'text-tinta-3'}`} title={previa}>
              {previa}
            </span>
            {canal.nao_lidas > 0 && (
              <span className="numero flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-acento px-1.5 text-xs font-semibold text-acento-tinta">
                <span aria-hidden>{canal.nao_lidas > 99 ? '99+' : canal.nao_lidas}</span>
                <span className="sr-only">{t('lista.naoLidas', { count: canal.nao_lidas })}</span>
              </span>
            )}
          </span>
        </span>
      </button>
    </li>
  );
}

function ResultadosBusca({ busca, aoEscolher }: { busca: string; aoEscolher: (canalId: string) => void }) {
  const { t } = useTranslation('equipe');
  const q = useQuery({ queryKey: ['equipe', 'busca', busca], queryFn: () => get<Pagina<ResultadoBusca>>('/equipe/busca', { q: busca, limite: 30 }), enabled: busca.trim().length >= 2 });
  const nomes = useNomesDoChat((q.data?.itens ?? []).flatMap((r) => [r.autor_nome, r.outro_nome]));
  if (!q.data?.itens.length) return null;
  return (
    <section aria-label={t('lista.mensagens')} className="border-t border-borda">
      <h3 className="kicker px-3 pt-3 pb-1">{t('lista.mensagens')}</h3>
      <ul className="divide-y divide-borda">
        {q.data.itens.map((r) => {
          // W20: pessoa por `nomePessoa`; nome de canal e texto pela censura com os nomes do chat.
          const onde = r.canal_tipo === 'direta' ? nomePessoa(r.outro_nome) : textoLivre(r.canal_nome ?? '', ...nomes);
          return (
            <li key={r.id}>
              <button type="button" data-resultado-busca onClick={() => aoEscolher(r.canal_id)} className="flex w-full min-w-0 flex-col gap-0.5 px-3 py-2.5 text-left hover:bg-sup-2">
                <span className="flex min-w-0 items-baseline justify-between gap-2 text-xs text-tinta-3">
                  <TextoCortado className="font-medium text-tinta-2">{`${onde} · ${nomePessoa(r.autor_nome)}`}</TextoCortado>
                  <span className="numero shrink-0">{quando(r.created_at)}</span>
                </span>
                <TextoCortado linhas={2} className="text-sm text-tinta-1">
                  {textoLivre(r.texto, ...nomes)}
                </TextoCortado>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Lista à esquerda: conversas diretas, grupos, departamentos e discussões, com busca nas conversas e nas mensagens. */
export function ListaCanais({ selecionado, aoEscolher, aoEstado, aoExplorar, aoNova }: { selecionado: string | null; aoEscolher: (id: string) => void; aoEstado?: (e: EstadoLista) => void; aoExplorar: () => void; aoNova?: () => void }) {
  const { t } = useTranslation('equipe');
  const { t: tn } = useTranslation();
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<FiltroLista>('todas');
  const buscaD = useDebounce(busca);
  const intervalo = useIntervaloEquipe(10_000);
  const params = {
    tipo: filtro === 'direta' || filtro === 'grupo' || filtro === 'departamento' ? filtro : undefined,
    nao_lidas: filtro === 'nao_lidas' || undefined,
    arquivados: filtro === 'arquivadas' || undefined,
    busca: buscaD.trim() || undefined,
  };
  const q = useInfiniteQuery({
    queryKey: ['equipe', 'canais', filtro, buscaD],
    queryFn: ({ pageParam }) => get<Pagina<Canal>>('/equipe/canais', { ...params, limite: 100, cursor: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
    refetchInterval: intervalo,
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  // W20: nomes que a prévia e o nome do canal podem citar (iniciais no modo discreto com a opção de nomes).
  const nomes = useNomesDoChat(itens.flatMap((c) => [c.outro?.nome, c.ultima?.autor_nome]));
  const estado: EstadoLista = q.isError ? 'erro' : q.data ? 'ok' : 'carregando';
  useEffect(() => {
    aoEstado?.(estado);
  }, [estado, aoEstado]);
  const buscando = buscaD.trim().length >= 2;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-2 border-b border-borda p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
          <Entrada type="search" aria-label={t('lista.buscar')} placeholder={t('lista.buscar')} className="pl-9" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <div role="group" aria-label={t('lista.filtros')} className="flex flex-wrap items-center gap-1.5">
          {FILTROS.map((f) => (
            <Filtro key={f.chave} ativo={filtro === f.chave} aoClicar={() => setFiltro(f.chave)}>
              {t(f.rotulo)}
            </Filtro>
          ))}
          <Botao variante="fantasma" tamanho="sm" className="ml-auto" icone={<Hash className="size-4" />} onClick={aoExplorar}>
            {t('acoes.canaisPublicos')}
          </Botao>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : itens.length === 0 && !buscando ? (
          filtro === 'todas' ? (
            <Vazio
              icone={<MessagesSquare className="size-5" aria-hidden />}
              titulo={t('lista.vaziaTitulo')}
              texto={t('lista.vaziaTexto')}
              acao={
                // Review r17 visual M2: secundário, como o "Novo assistente" do vazio de Meus assistentes (o primário é o do cabeçalho).
                aoNova && (
                  <Botao tamanho="sm" onClick={aoNova}>
                    {t('acoes.novaConversa')}
                  </Botao>
                )
              }
            />
          ) : (
            <Vazio texto={t('lista.vaziaFiltro')} />
          )
        ) : (
          <>
            {buscando && <h3 className="kicker px-3 pt-3 pb-1">{t('lista.conversas')}</h3>}
            {itens.length === 0 ? (
              <p className="px-3 py-2 text-sm text-tinta-3">{t('lista.semResultado')}</p>
            ) : (
              <ul className="divide-y divide-borda">
                {itens.map((c) => (
                  <ItemCanal key={c.id} canal={c} selecionado={c.id === selecionado} aoEscolher={() => aoEscolher(c.id)} nomes={nomes} />
                ))}
              </ul>
            )}
            {q.hasNextPage && (
              <div className="flex justify-center p-3">
                <Botao tamanho="sm" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
                  {tn('comum.carregarMais')}
                </Botao>
              </div>
            )}
            {buscando && <ResultadosBusca busca={buscaD} aoEscolher={aoEscolher} />}
          </>
        )}
      </div>
    </div>
  );
}
